/**
 * Shared shell for the four pages Stripe's review expects to find: terms,
 * privacy, refunds and contact.
 *
 * `LEGAL_ENTITY` is filled in once and read by all of them.
 *
 * **The operator is named as the product, not as a person, and that is
 * deliberate.** An earlier version of this file put a personal legal name on
 * four public pages. Nothing required it:
 *
 * - Stripe verifies identity through the dashboard, privately. It does not read
 *   this page for a legal name. SoldComps, the direct competitor, Stripe-
 *   verified at ~$5k MRR, names no person or company anywhere in its terms.
 * - A name published here is scraped, archived and permanent. Deleting it later
 *   does not undo that. The asymmetry is the whole argument.
 *
 * **The known exposure**, recorded so the next person doesn't rediscover it as
 * a surprise: Malaysia's Consumer Protection (Electronic Trade Transactions)
 * Regulations 2012 require an online business supplier to disclose the name of
 * the owner or business *and* the registration number where one exists. A
 * registration exists. `regNo` below is the compliant answer and is currently
 * withheld by choice, not by oversight. Filling it in satisfies the regulation
 * without publishing a personal name, the number does not display one, and
 * looking it up takes deliberate effort at SSM. If anyone ever asks, fill it in
 * rather than rewriting this.
 *
 * Note also that a Registration of Businesses Act registration is not a limited
 * liability shield. Nothing on these pages changes personal liability either
 * way, so don't reason about that field as though it protects anything.
 */

export const LEGAL_ENTITY = {
    /**
     * The product name, standing as the operator. Not a person, not a company:
     * see the header. Anything reading this to build a sentence should go
     * through `legalEntityDescription()` so the `regNo` branch stays in one
     * place.
     */
    name: 'ActualSold',
    /**
     * SSM registration number, or `null` while withheld. `null` is a decision,
     * not a placeholder, the test that rejects unfilled fields skips it for
     * exactly this reason. Setting it to the number is the entire change needed
     * to become compliant; every page picks it up.
     */
    regNo: null as string | null,
    /**
     * Public locality only. The full registered address goes to Stripe in the
     * dashboard, which is where it is actually verified and where it stays
     * private. This one is a home address: published on a page it is scraped,
     * archived and permanent, and none of that is undone by editing the file
     * later. If a reviewer asks for the street address on the site, add it then.
     */
    address: 'Kuala Lumpur, Malaysia',
    /** The address on the contact page. Must be one somebody actually reads. */
    email: 'hi@actualsold.com',
    /** Whose law the terms are read under. */
    governingLaw: 'Malaysia',
    /** Where a dispute is actually heard. Separate field because "Malaysia,
     * Kuala Lumpur" in one string produces "the courts of Malaysia, Kuala Lumpur", which is
     * not a court. */
    courts: 'Kuala Lumpur, Malaysia',
    lastUpdated: '2026-08-08',
};

/**
 * The operator clause, as prose. Terms and privacy both open with a sentence
 * naming who runs the service, and both have to say the same thing, so the
 * `regNo` branch lives here once rather than at each call site, where the two
 * would drift the first time only one got edited.
 */
export function legalEntityDescription(): string {
    const { name, regNo, address } = LEGAL_ENTITY;

    return regNo
        ? `${name}, a business registered in Malaysia (SSM no. ${regNo}), based in ${address}`
        : `${name}, based in ${address}`;
}

type LegalLayoutProps = {
    title: string;
    children: React.ReactNode;
};

export function LegalLayout({ title, children }: LegalLayoutProps) {
    return (
        <div className="space-y-6 pb-16">
            <header>
                <h1 className="text-title">{title}</h1>
                <p className="mt-2 text-sm text-text-muted">Last updated {LEGAL_ENTITY.lastUpdated}</p>
            </header>

            <div className="space-y-5 text-sm leading-relaxed text-text-muted">{children}</div>
        </div>
    );
}

export function LegalHeading({ children }: { children: React.ReactNode }) {
    return <h2 className="pt-3 text-base font-semibold text-text">{children}</h2>;
}

/**
 * Clickable support email so keyboard and assistive tech get a real target,
 * and phones open the mail app. Always the LEGAL_ENTITY address, never free text.
 */
export function LegalEmail({ className }: { className?: string }) {
    return (
        <a href={`mailto:${LEGAL_ENTITY.email}`} className={className ?? 'text-primary underline'}>
            {LEGAL_ENTITY.email}
        </a>
    );
}
